<template>
	<div class="resources_toggle_container">
		<button 
			@click="toggleResourcesExpansion"
			:class="[
				'resources_toggle_btn', 
				{ 
					'resources_toggle_btn--has-extras': hasExtraResources,
					'resources_toggle_btn--locked': hasExtraResources
				}
			]"
			:title="hasExtraResources ? 'Recursos extras selecionados - painel permanece aberto' : isExpanded ? 'Fechar recursos extras' : 'Abrir recursos extras'"
		>
			<div class="resources_toggle_content">
				<div class="resources_toggle_info">
					<div class="resources_toggle_icon_wrapper">
						<sw-icon name="stats-bars" class="resources_toggle_icon" />
					</div>
					<div class="resources_toggle_text_wrapper">
						<span class="resources_toggle_title">Recursos Extras</span>
						<span class="resources_toggle_subtitle">Crescer sob medida</span>
					</div>
				</div>
				<div class="resources_toggle_actions">
					<span v-if="hasExtraResources" class="extra_resources_indicator">
						+R$ {{ totalExtraResourcesCost.toFixed(2).replace('.', ',') }}/mês
					</span>
					<sw-icon 
						:name="isExpanded ? 'chevron-up' : 'chevron-down'" 
						class="resources_toggle_chevron" 
					/>
				</div>
			</div>
		</button>

		<!-- Expandable Resources Section -->
		<div v-if="isExpanded" class="plan_resources_expanded">
			<div class="resource_item_compact">
				<div class="resource_header_compact">
					<sw-icon name="database" class="resource_icon_small" />
					<span class="resource_label_small">Armazenamento</span>
				</div>
				<div class="resource_control_compact">
					<input 
						type="range" 
						:value="resources.storage" 
						@input="updateStorage(parseInt($event.target.value))"
						:min="storageMin" 
						:max="planId === 'essential' ? 200 : 1000"
						step="10"
						class="resource_range_small"
					/>
					<div class="resource_display_compact">
						<span class="resource_value_small">{{ resources.storage }} GB</span>
						<span v-if="storageExtraCost > 0" class="extra_cost_small">
							+R$ {{ storageExtraCost.toFixed(2).replace('.', ',') }}
						</span>
					</div>
				</div>
			</div>

			<div class="resource_item_compact">
				<div class="resource_header_compact">
					<sw-icon name="share" class="resource_icon_small" />
					<span class="resource_label_small">Páginas de Compartilhamento</span>
				</div>
				<div class="resource_control_compact">
					<input 
						type="range" 
						:value="resources.sharingPages" 
						@input="updateSharingPages(parseInt($event.target.value))"
						:min="sharingPagesMin" 
						:max="planId === 'essential' ? 50 : 500"
						step="10"
						class="resource_range_small"
					/>
					<div class="resource_display_compact">
						<span class="resource_value_small">{{ resources.sharingPages }} páginas web</span>
						<span v-if="sharingPagesExtraCost > 0" class="extra_cost_small">
							+R$ {{ sharingPagesExtraCost.toFixed(2).replace('.', ',') }}
						</span>
					</div>
				</div>
			</div>

			<div class="resource_item_compact">
				<div class="resource_header_compact">
					<sw-icon name="upload" class="resource_icon_small" />
					<span class="resource_label_small">Páginas de Upload</span>
				</div>
				<div class="resource_control_compact">
					<input 
						type="range" 
						:value="resources.uploadPages" 
						@input="updateUploadPages(parseInt($event.target.value))"
						:min="uploadPagesMin" 
						:max="planId === 'essential' ? 50 : 500"
						step="10"
						class="resource_range_small"
					/>
					<div class="resource_display_compact">
						<span class="resource_value_small">{{ resources.uploadPages }} páginas web</span>
						<span v-if="uploadPagesExtraCost > 0" class="extra_cost_small">
							+R$ {{ uploadPagesExtraCost.toFixed(2).replace('.', ',') }}
						</span>
					</div>
				</div>
			</div>

			<div class="resource_item_compact">
				<div class="resource_header_compact">
					<sw-icon name="mail" class="resource_icon_small" />
					<span class="resource_label_small">Envio de E-mails</span>
				</div>
				<div class="resource_control_compact">
					<input 
						type="range" 
						:value="resources.emailsSent" 
						@input="updateEmailsSent(parseInt($event.target.value))"
						:min="emailsSentMin" 
						:max="planId === 'essential' ? 600 : 10000"
						step="100"
						class="resource_range_small"
					/>
					<div class="resource_display_compact">
						<span class="resource_value_small">{{ resources.emailsSent }} e-mails/mês</span>
						<span v-if="emailsSentExtraCost > 0" class="extra_cost_small">
							+R$ {{ emailsSentExtraCost.toFixed(2).replace('.', ',') }}
						</span>
					</div>
				</div>
			</div>

			<div class="resource_item_compact">
				<div class="resource_header_compact">
					<sw-icon name="bell" class="resource_icon_small" />
					<span class="resource_label_small">Alertas</span>
				</div>
				<div class="resource_control_compact">
					<input 
						type="range" 
						:value="resources.alerts" 
						@input="updateAlerts(parseInt($event.target.value))"
						:min="alertsMin" 
						:max="planId === 'essential' ? 250 : 1000"
						step="10"
						class="resource_range_small"
					/>
					<div class="resource_display_compact">
						<span class="resource_value_small">{{ resources.alerts }} alertas/mês</span>
						<span v-if="alertsExtraCost > 0" class="extra_cost_small">
							+R$ {{ alertsExtraCost.toFixed(2).replace('.', ',') }}
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
	import { ref, computed, watch } from 'vue';

	const props = defineProps({
		planId: {
			type: String,
			required: true
		},
		initialResources: {
			type: Object,
			default: () => ({
				storage: 10,
				sharingPages: 10,
				uploadPages: 10,
				emailsSent: 100,
				alerts: 20
			})
		},
		baseResources: {
			type: Object,
			default: () => ({
				storage: 10,
				sharingPages: 10,
				uploadPages: 10,
				emailsSent: 100,
				alerts: 20
			})
		}
	});

	const emit = defineEmits(['update:resources', 'update:extra-cost']);

	// Estado local
	const manuallyExpanded = ref(false);
	const resources = ref({ ...props.initialResources });

	// Configurações de preços extras
	const STORAGE_PRICE_PER_10GB = 1.70;
	const PAGES_PRICE_PER_10 = 6.90;
	const EMAIL_PRICE_PER_100 = 0.80;
	const ALERT_PRICE_PER_10 = 0.45;

	// Computed para valores mínimos baseados no plano
	const storageMin = computed(() => props.baseResources.storage);
	const sharingPagesMin = computed(() => props.baseResources.sharingPages);
	const uploadPagesMin = computed(() => props.baseResources.uploadPages);
	const emailsSentMin = computed(() => props.baseResources.emailsSent);
	const alertsMin = computed(() => props.baseResources.alerts);

	// Factory function para calcular custos extras (DRY principle)
	const createCostCalculator = (resourceKey, baseKey, unit, pricePerUnit) => computed(() => {
		const extraUnits = Math.max(0, resources.value[resourceKey] - props.baseResources[baseKey]);
		return (extraUnits / unit) * pricePerUnit;
	});

	// Cost calculators gerados dinamicamente
	const storageExtraCost = createCostCalculator('storage', 'storage', 10, STORAGE_PRICE_PER_10GB);
	const sharingPagesExtraCost = createCostCalculator('sharingPages', 'sharingPages', 10, PAGES_PRICE_PER_10);
	const uploadPagesExtraCost = createCostCalculator('uploadPages', 'uploadPages', 10, PAGES_PRICE_PER_10);
	const emailsSentExtraCost = createCostCalculator('emailsSent', 'emailsSent', 100, EMAIL_PRICE_PER_100);
	const alertsExtraCost = createCostCalculator('alerts', 'alerts', 10, ALERT_PRICE_PER_10);

	// Total cost calculator usando functional approach
	const totalExtraResourcesCost = computed(() => {
		const costs = [storageExtraCost, sharingPagesExtraCost, uploadPagesExtraCost, emailsSentExtraCost, alertsExtraCost];
		return costs.reduce((total, cost) => total + cost.value, 0);
	});

	const hasExtraResources = computed(() => {
		return totalExtraResourcesCost.value > 0;
	});

	// Computed para controlar expansão - lógica inteligente
	const isExpanded = computed(() => {
		// Se tem recursos extras, sempre fica aberto
		// Se não tem recursos extras, segue o controle manual
		return hasExtraResources.value || manuallyExpanded.value;
	});

	// Métodos  
	const toggleResourcesExpansion = () => {
		// Early return pattern: mais limpo e direto
		if (hasExtraResources.value) {
			return console.info('💡 Recursos extras selecionados - painel permanece aberto');
		}

		// Toggle e log em uma operação fluida
		manuallyExpanded.value = !manuallyExpanded.value;
		console.debug(`🔧 Resources panel ${manuallyExpanded.value ? 'expanded' : 'collapsed'}`);
	};

	// Factory function para criar updaters de recursos
	const createResourceUpdater = (resourceKey) => (value) => {
		resources.value[resourceKey] = value;
		emitUpdates();
	};

	// Updaters gerados dinamicamente (DRY principle)
	const updateStorage = createResourceUpdater('storage');
	const updateSharingPages = createResourceUpdater('sharingPages');
	const updateUploadPages = createResourceUpdater('uploadPages');
	const updateEmailsSent = createResourceUpdater('emailsSent');
	const updateAlerts = createResourceUpdater('alerts');

	const emitUpdates = () => {
		emit('update:resources', resources.value);
		emit('update:extra-cost', totalExtraResourcesCost.value);
	};

	// Sincronização otimizada com props
	watch(() => props.initialResources, (newResources) => {
		// Apenas atualiza se realmente mudou (evita rerenders desnecessários)
		if (JSON.stringify(resources.value) !== JSON.stringify(newResources)) {
			resources.value = { ...newResources };
		}
	}, { deep: true });


	// Emit inicial
	emitUpdates();
</script>

<style lang="scss" scoped>
	// Resources Toggle Styles
	.resources_toggle_container {
		margin-bottom: 1.25rem;
	}

	.resources_toggle_btn {
		width: 100%;
		padding: 1.25rem 1.5rem;
		background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
		border: none;
		border-radius: 1rem;
		cursor: pointer;
		transition: all 0.3s ease;
		box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
		position: relative;
		overflow: hidden;

		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: -100%;
			width: 100%;
			height: 100%;
			background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
			transition: left 0.5s ease;
		}

		&:hover {
			background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
			transform: translateY(-2px) scale(1.02);
			box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);

			&::before {
				left: 100%;
			}
		}

		&:active {
			transform: translateY(-1px) scale(1.01);
		}

		// Estado especial quando há recursos extras selecionados
		&--has-extras {
			// Visual feedback diferente quando há recursos extras
			box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
		}

		// Estado bloqueado quando há recursos extras (não permite fechar)
		&--locked {
			cursor: help; // Indica que há informação adicional
			
			&:hover {
				// Efeito de hover mais suave - indica estado "bloqueado"
				background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
				transform: translateY(-1px) scale(1.005); // Movimento mais sutil
				box-shadow: 0 6px 20px rgba(102, 126, 234, 0.35);
				
				&::before {
					left: 50%; // Animação mais sutil
				}
			}
			
			&:active {
				transform: translateY(0px) scale(1.002); // Feedback visual mínimo
			}

			// Adiciona um indicador visual sutil de "locked"
			&::after {
				content: '🔒';
				position: absolute;
				top: 0.5rem;
				right: 0.5rem;
				font-size: 0.75rem;
				opacity: 0.7;
				pointer-events: none;
			}
		}
	}

	.resources_toggle_content {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.resources_toggle_info {
		display: flex;
		align-items: center;
		gap: 1rem;
	}

	.resources_toggle_icon_wrapper {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 3rem;
		height: 3rem;
		background: rgba(255, 255, 255, 0.2);
		border-radius: 0.75rem;
		backdrop-filter: blur(10px);
	}

	.resources_toggle_icon {
		width: 1.5rem;
		height: 1.5rem;
		color: #ffffff;
		filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
	}

	.resources_toggle_text_wrapper {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 0.125rem;
	}

	.resources_toggle_title {
		font-size: 1.125rem;
		font-weight: 700;
		color: #ffffff;
		text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
		line-height: 1.2;
	}

	.resources_toggle_subtitle {
		font-size: 0.875rem;
		font-weight: 500;
		color: rgba(255, 255, 255, 0.8);
		text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
		line-height: 1.2;
	}

	.resources_toggle_actions {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		flex-shrink: 0;
	}

	.extra_resources_indicator {
		font-size: 0.75rem;
		font-weight: 600;
		color: #ffffff;
		background: rgba(255, 255, 255, 0.25);
		padding: 0.25rem 0.625rem;
		border-radius: 0.5rem;
		border: 1px solid rgba(255, 255, 255, 0.3);
		backdrop-filter: blur(10px);
		text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		white-space: nowrap;
		flex-shrink: 0;
	}

	.resources_toggle_chevron {
		width: 1.125rem;
		height: 1.125rem;
		color: rgba(255, 255, 255, 0.9);
		transition: all 0.3s ease;
		filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));

		.resources_toggle_btn:hover & {
			transform: scale(1.1);
		}
	}

	.plan_resources_expanded {
		margin-top: 1rem;
		padding: 1.25rem;
		background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
		border-radius: 0.75rem;
		border: 1px solid #e2e8f0;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
		animation: fadeIn 0.3s ease-in-out;
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: translateY(-10px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	// Compact Resource Styles
	.resource_item_compact {
		&:not(:last-child) {
			margin-bottom: 1rem;
		}
	}

	.resource_header_compact {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		margin-bottom: 0.5rem;
	}

	.resource_icon_small {
		width: 1rem;
		height: 1rem;
		color: #667eea;
		font-size: 0.875rem;
	}

	.resource_label_small {
		font-size: 0.875rem;
		font-weight: 500;
		color: #374151;
	}

	.resource_control_compact {
		display: flex;
		flex-direction: column;
		gap: 0.375rem;
	}

	.resource_range_small {
		width: 100%;
		height: 10px;
		border-radius: 5px;
		background: linear-gradient(to right, #e2e8f0 0%, #cbd5e1 100%);
		outline: none;
		-webkit-appearance: none;
		appearance: none;
		transition: all 0.2s ease;

		&:hover {
			background: linear-gradient(to right, #cbd5e1 0%, #94a3b8 100%);
		}

		&::-webkit-slider-thumb {
			-webkit-appearance: none;
			appearance: none;
			width: 18px;
			height: 18px;
			border-radius: 50%;
			background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
			cursor: pointer;
			box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
			transition: all 0.2s ease;

			&:hover {
				transform: scale(1.1);
				box-shadow: 0 3px 12px rgba(102, 126, 234, 0.5);
			}
		}

		&::-moz-range-thumb {
			width: 18px;
			height: 18px;
			border-radius: 50%;
			background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
			cursor: pointer;
			border: none;
			box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
			transition: all 0.2s ease;
		}
	}

	.resource_display_compact {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.resource_value_small {
		font-size: 0.875rem;
		font-weight: 600;
		color: #111827;
	}

	.extra_cost_small {
		font-size: 0.75rem;
		font-weight: 600;
		color: #059669;
		background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%);
		padding: 0.1875rem 0.5rem;
		border-radius: 0.375rem;
		border: 1px solid rgba(16, 185, 129, 0.2);
		box-shadow: 0 1px 2px rgba(16, 185, 129, 0.1);
	}
</style>